import { useState } from 'react'
import { FileText, Image, Download, Unlink, Loader2 } from 'lucide-react'
import { Badge } from '../Badge'
import type { ExpenseDocument, DocumentType } from '@/api/client'
import { api } from '@/api/client'

const DOC_TYPE_LABELS: Record<DocumentType, string> = {
  Receipt: 'Receipt',
  ProofOfPayment: 'Proof of Payment',
  LetterOfMedicalNecessity: 'LMN',
  ExplanationOfBenefits: 'EOB',
  Other: 'Other',
}

const DOC_TYPE_VARIANT: Record<DocumentType, 'navy' | 'gold' | 'green' | 'gray'> = {
  Receipt: 'navy',
  ProofOfPayment: 'green',
  LetterOfMedicalNecessity: 'gold',
  ExplanationOfBenefits: 'gray',
  Other: 'gray',
}

interface DocumentListProps {
  expenseId: string
  documents: ExpenseDocument[]
  onDocumentDeleted: (documentId: string) => void
}

export function DocumentList({ expenseId, documents, onDocumentDeleted }: DocumentListProps) {
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null)
  const [confirmUnlinkId, setConfirmUnlinkId] = useState<string | null>(null)

  const handleUnlink = async (documentId: string) => {
    setUnlinkingId(documentId)
    try {
      await api.deleteDocument(expenseId, documentId)
      onDocumentDeleted(documentId)
    } catch (err) {
      console.error('Failed to unlink document:', err)
    } finally {
      setUnlinkingId(null)
      setConfirmUnlinkId(null)
    }
  }

  const handleDownload = (documentId: string) => {
    const url = api.getDocumentDownloadUrl(expenseId, documentId)
    window.open(url, '_blank')
  }

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    })
  }

  const fileIcon = (contentType: string) =>
    contentType.startsWith('image/')
      ? <Image size={18} className="text-[#718096]" />
      : <FileText size={18} className="text-[#718096]" />

  if (documents.length === 0) {
    return (
      <div className="text-[13px] text-[#A0AEC0] text-center py-4">
        No documents attached yet.
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {documents.map((doc) => (
        <div
          key={doc.id}
          className="flex items-center gap-3 p-3 rounded-lg border border-[#E2E8F0] bg-white hover:bg-[#F7FAFC] transition-colors"
        >
          {fileIcon(doc.contentType)}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-medium text-[#1B365D] truncate">{doc.fileName}</span>
              <Badge
                variant={DOC_TYPE_VARIANT[doc.documentType] || 'gray'}
                size="category"
              >
                {DOC_TYPE_LABELS[doc.documentType] || doc.documentType}
              </Badge>
            </div>
            <div className="text-[11px] text-[#A0AEC0]">
              {formatSize(doc.fileSize)} &middot; {formatDate(doc.createdAt)}
              {doc.description && <> &middot; {doc.description}</>}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleDownload(doc.id)}
              className="p-1.5 text-[#718096] hover:text-[#1B365D] rounded hover:bg-[#EDF2F7] transition-colors"
              title="Download"
            >
              <Download size={14} />
            </button>
            {confirmUnlinkId === doc.id ? (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleUnlink(doc.id)}
                  disabled={unlinkingId === doc.id}
                  className="text-[11px] text-[#C9A227] hover:text-[#B8911F] px-2 py-1 rounded hover:bg-[#C9A227]/10"
                >
                  {unlinkingId === doc.id ? <Loader2 size={12} className="animate-spin" /> : 'Unlink'}
                </button>
                <button
                  onClick={() => setConfirmUnlinkId(null)}
                  className="text-[11px] text-[#718096] hover:text-[#4A5568] px-1 py-1"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmUnlinkId(doc.id)}
                className="p-1.5 text-[#718096] hover:text-[#C9A227] rounded hover:bg-[#C9A227]/10 transition-colors"
                title="Unlink from expense"
              >
                <Unlink size={14} />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
